/**
 * Pure helpers shared by every rename in the app (app#144, app#177,
 * nobgp-router#1001).
 *
 * They live here rather than inside the components because this repo has no test
 * runner — the convention is to extract the logic worth checking and exercise it
 * with `node --experimental-strip-types`. See renameName.test.ts beside this file.
 *
 * ⚠ These were node-only (`nodeName.ts`) until app#177. The network rename wrote
 * straight to PostgREST with no no-op guard and put raw Postgres text in an
 * `alert()`; the three renames now share the guard and the message.
 */

/** What is being renamed. Only the wording of a message depends on it. */
export type RenameSubject = 'node' | 'network' | 'organization'

/**
 * A readable message for a failed ORGANIZATION rename.
 *
 * ⚠⚠ THE ORGANIZATION IS THE ONE SUBJECT WITH NO SQLSTATE, so none of the Postgres
 * branches below can help it. `organizations` has no UPDATE policy for
 * `authenticated` — `org_read` is SELECT only — so the write cannot go to PostgREST
 * at all. It goes to the router's `org_update` tool, and `callTool` throws a plain
 * `new Error(err.message)`: no `code`, no constraint name, only the ROUTER's own
 * words. So these match the router's sentences, and the caller must never fall into
 * a Postgres branch by accident.
 *
 * ⚠ THERE IS NO UNIQUE INDEX ON `organizations.name`. Two organizations may share a
 * name, so "that name is taken" is not a failure this subject has. The only name
 * constraint the schema carries is `organizations_name_required`
 * — CHECK (personal OR name IS NOT NULL) — and the router refuses an empty name
 * before the UPDATE runs, so that branch is a guard against a future caller that
 * forgets, not a live path. The other two unique keys on the table
 * (`organizations_slug_key`, `organizations_workos_org_id_key`) are not on the name
 * and a rename does not touch them.
 */
function organizationRenameMessage(text: string): string {
  // requireRole answers `"members.manage requires a higher role in this organization"`
  // (tools/authz.go). Owner and Admin may rename; a Member may not.
  if (/requires a higher role/i.test(text) || /permission denied|row-level security/i.test(text)) {
    return 'You do not have permission to rename this organization.'
  }
  // ⚠ The NUMBER comes from the router's own sentence, never from a copy of
  // `orgNameMaxLen` kept here. A limit that moves in `org_create.go` would otherwise
  // leave this telling the customer to shorten a name the router accepts.
  const tooLong = /at most (\d+) characters/i.exec(text)
  if (tooLong) {
    return `That name is too long. Use ${tooLong[1]} characters or fewer.`
  }
  if (/must not be empty/i.test(text) || text.includes('organizations_name_required')) {
    return 'An organization must have a name.'
  }
  return text || 'Could not rename the organization.'
}

/**
 * Whether a submitted name is a real change worth writing.
 *
 * ⚠ A no-op rename is not harmless. The write reaches PostgREST, and on the
 * router side a name change is what a node's own reconnect has to reconcile —
 * so submitting the unchanged name on every blur turns an idle form into fleet
 * traffic. The org rename beside this makes the same check for the same reason.
 *
 * Trims, and compares CASE-SENSITIVELY.
 *
 * ⚠⚠ THIS USED TO LOWERCASE BOTH SIDES, ON A PREMISE THAT IS FALSE IN BOTH HALVES.
 * The claim was that `nodes_network_id_unique_active` is case-insensitive and that
 * the router lowercases on lookup. Neither is true: the index is plain
 * `(network_id, name)` with no `lower()` (schema.sql), and the router's
 * `GetNodeByNetworkIdAndName` matches `name = ?`. So `GLKVM` and `glkvm` are two
 * different names to the database and to the lookup alike.
 *
 * The cost of the old behaviour was a silent no-op: node names may carry uppercase
 * (the router's own NodeNameRegexp allows `[a-zA-Z0-9]`), so a node registered as
 * `GLKVM` could never be renamed to `glkvm` — this returned false, the caller
 * returned early, and the user saw no write and no error.
 */
export function isRealRename(current: string, next: string): boolean {
  const trimmed = next.trim()
  if (!trimmed) return false
  return trimmed !== current.trim()
}

/**
 * A readable message for a failed rename.
 *
 * ⚠ THE UNIQUE VIOLATION IS THE EXPECTED FAILURE, NOT AN EDGE CASE. A node name is
 * unique per network and a network name is unique per owner, so "that name is
 * taken" is the outcome any user renaming to a name they already use will hit —
 * and the raw Postgres text for it is `duplicate key value violates unique
 * constraint "nodes_network_id_unique_active"`, which tells them nothing and looks
 * like a crash.
 *
 * Matches on the SQLSTATE (23505) and falls back to the constraint name, since
 * PostgREST returns the code on `error.code` but a bare Error carries only text.
 *
 * ⚠ The node and the network need two different sentences, because the two indexes
 * have different scopes: `nodes_network_id_unique_active` is per NETWORK, and
 * `unique_network_name` is `(owner_id, name)` — per OWNER, not per org. Telling an
 * Admin that "this org already has that network" would be wrong: the clash is with
 * a network the OWNER holds, which may not be one this Admin can see.
 *
 * ⚠⚠ The ORGANIZATION leaves through the first branch and reaches none of the
 * Postgres branches. Its rename goes to the router, not to PostgREST, so every
 * failure is a message with no SQLSTATE. See organizationRenameMessage above.
 */
export function renameErrorMessage(error: unknown, subject: RenameSubject = 'node'): string {
  const code = (error as { code?: string } | null)?.code
  const text = (error as { message?: string } | null)?.message ?? ''

  // ⚠ The organization returns EARLY and shares none of the branches below. Its
  // failures arrive as router prose, and a bare `code === '23505'` here would tell
  // an org user their name is taken — which cannot happen, because
  // `organizations.name` has no unique index. See organizationRenameMessage.
  if (subject === 'organization') return organizationRenameMessage(text)

  // ⚠ The node and the network rename go through router tools since app#373 (`node_rename`,
  // `network_update`), so their failures arrive as a ToolCallError `code`: `already_exists` for a
  // name that is taken, `forbidden` for a reader who is not the owner or an org Owner/Admin. The
  // Postgres branches below stay for the node CHANNEL, which is still a PostgREST write.
  if (code === 'forbidden') {
    return `Only the owner of this ${subject}, or an owner or admin of its organization, can rename it.`
  }
  if (code === 'already_exists' || code === '23505' || text.includes('nodes_network_id_unique_active') || text.includes('unique_network_name')) {
    return subject === 'network'
      ? 'That name is already used by another network with the same owner.'
      : 'That name is already used by another node.'
  }
  // ⚠⚠ THE HOLD IS STILL IN FORCE UNTIL MIGRATION 0126 IS APPLIED, and until then this
  // is the failure EVERY user gets, not an edge case. `0121`'s trigger refuses a
  // `nodes.name` write from `authenticated`/`anon` and raises a plain `raise exception`
  // — SQLSTATE P0001 — carrying an explanation written for us and not for a customer
  // ("a node name is its identity, not metadata … splits the node in two (router#1001)").
  // Without this branch the fallback below puts that sentence on screen verbatim.
  // ⚠ Matched on the TRIGGER NAME as well as the text, because the wording is prose in
  // a migration and prose gets edited.
  if (text.includes('nodes_name_router_only') || text.includes('a node name is its identity')) {
    return 'Renaming a node is not available yet. It unlocks once the routers have been updated.'
  }
  if (code === '42501' || /permission denied|row-level security/i.test(text)) {
    return `You do not have permission to rename this ${subject}.`
  }
  // `networks_name_trimmed` CHECK (name = trim(name)). Both callers trim before they
  // write, so this is a guard against a future caller that forgets, not a live path.
  if (code === '23514' || text.includes('networks_name_trimmed')) {
    return 'That name has a space at the start or the end.'
  }
  return text || `Could not rename the ${subject}.`
}
